import {useCallback, useRef, useState} from 'react'
import { Box, Typography } from '@mui/material'

import { median, sum } from '../utils/Array'
import { parseIncursionPayouts } from '../model/IncursionPayouts'
import { getSitesFromPayouts } from '../model/IncursionStats'
import style from './IncursionCalculator.module.css'

const IncursionCalculator = () => {

  const generateStatsButton = useRef<HTMLButtonElement>(null)

  const [payouts, setPayouts] = useState('')
  const [totalSiteCount, setTotalSiteCount] = useState<number | string>('unknown')
  const [averageSiteTime, setAverageSiteTime] = useState<number | string>('unknown')
  const [medianSiteTime, setMedianSiteTime] = useState<number | string>('unknown')
  const [minimumSiteTime, setMinimumSiteTime] = useState<number | string>('unknown')
  const [maximumSiteTime, setMaximumSiteTime] = useState<number | string>('unknown')
  const [totalPayout, setTotalPayout] = useState<number | string>('unknown')
  const [hourlyPayout, setHourlyPayout] = useState<number | string>('unknown')

  const calculateStats = () => {
    const payoutItems = parseIncursionPayouts(payouts)
    const sites = getSitesFromPayouts(payoutItems)
    setTotalSiteCount(payoutItems.length)
    setTotalPayout(sum(payoutItems.map(item => item.isk)))
    if (sites.length < 1) {
      setAverageSiteTime('unknown')
      setMedianSiteTime('unknown')
      setMinimumSiteTime('unknown')
      setMaximumSiteTime('unknown')
      setHourlyPayout('unknown')
    } else {
      setAverageSiteTime(sum(sites.map(s => s.minutes)) * 60 / sites.length)
      setMedianSiteTime(median(sites.map(s => s.minutes)) * 60)
      setMinimumSiteTime(sites.map(s => s.minutes).reduce((p, c) => (c < p) ? c : p) * 60)
      setMaximumSiteTime(sites.map(s => s.minutes).reduce((p, c) => (c > p) ? c : p) * 60)
      setHourlyPayout(sum(payoutItems.map(item => item.isk)) / ((sum(sites.map(s => s.minutes)) / sites.length * payoutItems.length) / 60))
    }
  }

  const activateButton = useCallback(event => {
    setPayouts(event.clipboardData.getData('text/plain'))
    generateStatsButton.current?.focus()
  }, [setPayouts, generateStatsButton])

  return <div className={style.app}>
    <div className={style.textInput}>
      <textarea className={style.textArea} value={payouts} onChange={(event) => setPayouts(event.target.value)} onPaste={activateButton}/>
      <button className={style.generateStatsButton} ref={generateStatsButton} onClick={calculateStats}>Generate Stats</button>
      <Typography className={style.description}>
        This tools calculates some statistics from your Incursion payouts.
        Open your in-game wallet, select the payout entries (recognizable by the type “Corporate Reward Payout”), copy them to the clipboard, and paste them into the text field above.
        Clicking the “Generate Status Button” will then fill the table to the right with the appropriate values.
      </Typography>
      <Typography className={style.description}>
        If this app is useful to you, or if you think it should do more, don’t hesitate to contact me via in-game mail to <em>Ajaqa Chajij</em>.
        You can also send ISK! 😁
      </Typography>
    </div>
    <div className={style.stats}>
      <Typography variant='h2'>Incursion Statistics</Typography>
      <div>Total Sites: <Box className={style.value} component='span' aria-label='sites-count-total'>{totalSiteCount}</Box></div>
      <div>Average Site Time: <Box className={style.value} component='span' aria-label='sites-time-average'>{formatDuration(averageSiteTime)}</Box></div>
      <div>Median Site Time: <Box className={style.value} component='span' aria-label='sites-time-median'>{formatDuration(medianSiteTime)}</Box></div>
      <div>Fastest Site: <Box className={style.value} component='span' aria-label='sites-time-minimum'>{formatDuration(minimumSiteTime)}</Box></div>
      <div>Slowest Site: <Box className={style.value} component='span' aria-label='sites-time-maximum'>{formatDuration(maximumSiteTime)}</Box></div>
      <div>Total Payout: <Box className={style.value} component='span' aria-label='payout-total'>{formatMoney(totalPayout, 'ISK')}</Box></div>
      <div>Hourly Payout: <Box className={style.value} component='span' aria-label='payout-hourly'>{formatMoney(hourlyPayout, 'ISK/h')}</Box></div>
    </div>
  </div>
}

const formatDuration = (seconds: number | string): string => {
  if (typeof seconds === "string") {
    return seconds
  }
  const times = [
    [Math.floor(seconds / 86400), 'd'],
    [Math.floor((seconds / 3600) % 24), 'h'],
    [Math.floor((seconds / 60) % 60), 'm'],
    [Math.floor(seconds) % 60, 's']
  ]
  const formattedTime = times.filter(t => t[0] !== 0).map(t => `${t[0]}${t[1]}`).join(' ')
  if (formattedTime.length === 0) {
    return '0s'
  }
  return formattedTime
}

const formatMoney = (amount: number | string, unit: string = 'ISK'): string =>
  (typeof amount === "string") ? amount : (Intl.NumberFormat('en-US', { useGrouping: true, maximumFractionDigits: 0 }).format(amount) + " " + unit)

export default IncursionCalculator
